import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Coins, Minus, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGET_MIN = 10;
const BUDGET_MAX = 40;

export function TalerCalibrationCard({ onApplied }) {
    const { language } = useLanguage();
    const { user } = useAuth();
    const de = language === 'de';
    const [data, setData] = useState(null);
    const [chosen, setChosen] = useState(18);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!user) return;
        axios.get(`${API}/taler/calibration`)
            .then(res => {
                setData(res.data);
                if (res.data?.eligible) setChosen(res.data.suggested_budget);
            })
            .catch(() => setData(null));
    }, [user]);

    if (done || !data?.eligible) return null;

    const { current_budget, suggested_budget, avg_net, active_days, days_over } = data;
    const suggestsChange = suggested_budget !== current_budget;

    const respond = async (action, budget) => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/taler/calibration/respond`, { action, budget });
            setDone(true);
            const b = res.data.budget;
            toast.success(action === 'keep'
                ? (de ? `Alles bleibt bei ${b} Talern – Nanouk ist zufrieden! 🐺` : `Staying at ${b} Taler – Nanouk approves! 🐺`)
                : (de ? `Neues Tagesbudget: ${b} Taler 🪙` : `New daily budget: ${b} Taler 🪙`));
            if (action !== 'keep') onApplied?.(b);
        } catch {
            toast.error(de ? 'Speichern fehlgeschlagen' : 'Failed to save');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50/60" data-testid="taler-calibration-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-start gap-3">
                    <img src="/nanouk/energy_balanced.png" alt="Nanouk" className="w-14 h-14 object-contain shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold flex items-center gap-1.5" data-testid="taler-calibration-title">
                            <Coins className="w-4 h-4 text-amber-600" />
                            {de ? 'Eine Woche im Taler-Modus! 🎉' : 'One week in Taler mode! 🎉'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 leading-snug" data-testid="taler-calibration-message">
                            {de
                                ? <>Du warst an <strong>{active_days} Tagen</strong> aktiv und hast netto Ø <strong>{avg_net} Taler/Tag</strong> verbraucht{days_over > 0 ? ` (${days_over}× über Budget)` : ''}. {suggestsChange
                                    ? <>Mein Vorschlag: <strong>{suggested_budget} Taler</strong> statt {current_budget}.</>
                                    : <>Dein {current_budget}er-Budget passt richtig gut zu dir!</>}</>
                                : <>You were active on <strong>{active_days} days</strong> with a net average of <strong>{avg_net} Taler/day</strong>{days_over > 0 ? ` (over budget ${days_over}×)` : ''}. {suggestsChange
                                    ? <>My suggestion: <strong>{suggested_budget} Taler</strong> instead of {current_budget}.</>
                                    : <>Your budget of {current_budget} fits you really well!</>}</>}
                        </p>
                    </div>
                </div>

                <div className="flex items-center justify-center gap-4 py-1">
                    <button
                        onClick={() => setChosen(c => Math.max(BUDGET_MIN, c - 1))}
                        disabled={busy || chosen <= BUDGET_MIN}
                        className="w-9 h-9 rounded-full bg-white border border-amber-300 flex items-center justify-center text-amber-700 disabled:opacity-40 active:scale-95 transition-transform"
                        data-testid="taler-calibration-minus"
                    >
                        <Minus className="w-4 h-4" />
                    </button>
                    <div className="text-center min-w-[72px]">
                        <span className="text-3xl font-bold text-amber-700" data-testid="taler-calibration-value">{chosen}</span>
                        <p className="text-[10px] text-muted-foreground -mt-0.5">{de ? 'Taler / Tag' : 'Taler / day'}</p>
                    </div>
                    <button
                        onClick={() => setChosen(c => Math.min(BUDGET_MAX, c + 1))}
                        disabled={busy || chosen >= BUDGET_MAX}
                        className="w-9 h-9 rounded-full bg-white border border-amber-300 flex items-center justify-center text-amber-700 disabled:opacity-40 active:scale-95 transition-transform"
                        data-testid="taler-calibration-plus"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex gap-2">
                    <Button
                        className="flex-1 h-9 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
                        disabled={busy}
                        onClick={() => respond(chosen === suggested_budget ? 'accept' : 'custom', chosen)}
                        data-testid="taler-calibration-apply"
                    >
                        <Check className="w-4 h-4 mr-1" />
                        {de ? `${chosen} Taler übernehmen` : `Apply ${chosen} Taler`}
                    </Button>
                    <Button
                        variant="outline"
                        className="flex-1 h-9 rounded-full border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-100"
                        disabled={busy}
                        onClick={() => respond('keep')}
                        data-testid="taler-calibration-keep"
                    >
                        {de ? `Passt so – ${current_budget} behalten` : `It fits – keep ${current_budget}`}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
